import {
  asArray,
  mapImage,
  mapText,
  mapTitle,
  mapVideo,
  normalizeSpacer,
} from "@/components/DesignSystem/utils/teaserHelper"
import { handleVideoClick } from "../analytics/videoSliderAnalytics"

/**
 * Maps a “Video Slider” container into KDS props.
 *
 * Selects the first child from `:itemsOrder` (or first key in `:items`) and maps:
 * - `mod` (defaults to "white")
 * - `heading` via `mapTitle(basic.heading)`
 * - `topSpacer` / `bottomSpacer` via `normalizeSpacer` (read from node-level)
 * - `cards[]` from `media` (supports array or single object) where each card includes:
 *    - `heading` via `mapTitle(mediaItem.description || 'lorem ipsum')`
 *    - `video` via `mapVideo(mediaItem.video)` (with `closeCTA` passthrough)
 *    - `image` via `mapImage(mediaItem.image)`
 *    - `duration` via `mapText(mediaItem.duration)`
 *  A card is included if it has a `heading` AND (`video` OR `image`).
 *
 * Returns `null` if no child node exists. Omits empty keys.
 *
 * @param {Object} container AEM container node with `:itemsOrder` and `:items`.
 * @return {Object|null} Mapped props or `null`.
 */
export function mapVideoSliderProps(container) {
  const firstKey =
    container?.[":itemsOrder"]?.[0] ??
    Object.keys(container?.[":items"] ?? {})[0]

  const node = firstKey ? container?.[":items"]?.[firstKey] : undefined
  if (!node) return null

  const basic = node.basic || {}
  const mediaList = asArray(node?.media?.media ?? node?.media ?? [])
  const mod = basic?.mod || "white"
  const heading = mapTitle(basic?.heading)

  const topSpacer = node?.topSpacer
    ? normalizeSpacer(node.topSpacer.size ?? node.topSpacer)
    : undefined
  const bottomSpacer = node?.bottomSpacer
    ? normalizeSpacer(node.bottomSpacer.size ?? node.bottomSpacer)
    : undefined

  const cards = mediaList
    .map(c => {
      const videoRaw = asArray(c?.video)?.[0]
      const imageRaw = asArray(c?.image)?.[0]

      const videoSrc = videoRaw?.src
      const isScene7Image =
        typeof videoSrc === "string" && videoSrc.includes("is/image")
      const video = !isScene7Image && videoRaw ? mapVideo(videoRaw) : undefined
      const imageCandidate = isScene7Image ? videoRaw : imageRaw
      const image = imageCandidate ? mapImage(imageCandidate) : undefined
      const title = mapTitle(c?.heading?.[0])
      const duration = mapText(c?.duration)

      const closeCTA = videoRaw?.closeCTA
      // closeCTA.onClick = () => console.log("Close video")

      return title && (video || image)
        ? {
            heading: title,
            ...(video ? { video, closeCTA } : {}),
            ...(image ? { image } : {}),
            ...(duration ? { duration } : {}),
            onClick: () =>
              handleVideoClick(
                heading.text,
                title.text,
                `${title.text}_video`,
                videoSrc
              ),
          }
        : undefined
    })
    .filter(Boolean)

  if (cards.length === 0) return null

  return {
    ...(mod ? { mod } : {}),
    ...(heading ? { heading } : {}),
    ...(cards.length ? { cards } : {}),
    ...(topSpacer ? { topSpacer } : {}),
    ...(bottomSpacer ? { bottomSpacer } : {}),
  }
}
